/**
 * teaserAudioManager.js
 * Coordinates teaser video audio playback between YowBanner and YowCard hover previews.
 * Invariant: At most ONE teaser can play unmuted at any given time across the application.
 */

export const TEASER_AUDIO_EVENT = 'yowtv_teaser_audio_change';

// In-memory tracking of active unmuted teaser source ('banner' | 'card' | null)
let currentUnmutedSource = null;
let currentUnmutedCardId = null;

// Track user preference for card teasers (if user deliberately unmuted a card teaser,
// future card hover previews can start unmuted, and banner will be muted on start)
let cardTeaserSoundPreference = false;

/**
 * Check if card teasers are preferred to play unmuted on start.
 */
export const getCardTeaserSoundPreference = () => cardTeaserSoundPreference;

/**
 * Set the preference for card teaser audio.
 */
export const setCardTeaserSoundPreference = (enabled) => {
  cardTeaserSoundPreference = Boolean(enabled);
};

/**
 * Get currently active unmuted teaser info.
 */
export const getActiveUnmutedSource = () => currentUnmutedSource;

/**
 * Check if a card teaser is currently playing unmuted.
 */
export const isCardTeaserPlayingUnmuted = () => currentUnmutedSource === 'card';

/**
 * Check if the banner teaser is currently playing unmuted.
 */
export const isBannerTeaserPlayingUnmuted = () => currentUnmutedSource === 'banner';

/**
 * Notify that a teaser (banner or card) has started playing unmuted, or was unmuted by the user.
 * @param {'banner' | 'card'} source - Which component is unmuted
 * @param {string|number} [id] - Optional ID of the item/show
 */
export const notifyTeaserUnmuted = (source, id = null) => {
  currentUnmutedSource = source;
  if (source === 'card') {
    currentUnmutedCardId = id;
    cardTeaserSoundPreference = true;
  } else if (source === 'banner') {
    currentUnmutedCardId = null;
    cardTeaserSoundPreference = false;
  }

  // Dispatch custom event for React components to update state
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(TEASER_AUDIO_EVENT, {
        detail: {
          activeSource: source,
          id,
          isMuted: false,
        },
      })
    );

    // Direct DOM-level enforcement as an immediate safety net:
    // Mute any competing video elements directly in the DOM
    try {
      if (source === 'card') {
        // Mute banner video element(s)
        const bannerVideos = document.querySelectorAll(
          'video.trailerVideo, .yowBanner video, .yowBanner .bgImageContainer video'
        );
        bannerVideos.forEach((v) => {
          v.muted = true;
          v.defaultMuted = true;
        });
      } else if (source === 'banner') {
        // Mute all card hover modal video elements
        const cardVideos = document.querySelectorAll(
          'video.modalTrailerVideo, .yowHoverPreviewModal video, .yowCardContainer video'
        );
        cardVideos.forEach((v) => {
          v.muted = true;
          v.defaultMuted = true;
        });
      }
    } catch (err) {
      console.error('Error in DOM audio mute sync:', err);
    }
  }
};

/**
 * Notify that a teaser has been muted or stopped.
 * @param {'banner' | 'card'} source
 * @param {string|number} [id]
 */
export const notifyTeaserMuted = (source, id = null) => {
  if (source === 'card') {
    if (id === null || currentUnmutedCardId === id) {
      if (currentUnmutedSource === 'card') {
        currentUnmutedSource = null;
        currentUnmutedCardId = null;
      }
    }
  } else if (source === 'banner') {
    if (currentUnmutedSource === 'banner') {
      currentUnmutedSource = null;
    }
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(TEASER_AUDIO_EVENT, {
        detail: {
          activeSource: null,
          mutedSource: source,
          id,
          isMuted: true,
        },
      })
    );
  }
};

/**
 * Force mute all teasers (e.g. on unmount or navigation).
 */
export const muteAllTeasers = () => {
  currentUnmutedSource = null;
  currentUnmutedCardId = null;

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(TEASER_AUDIO_EVENT, {
        detail: {
          activeSource: null,
          mutedSource: 'all',
          isMuted: true,
        },
      })
    );

    try {
      const allVideos = document.querySelectorAll(
        'video.trailerVideo, video.modalTrailerVideo, .yowBanner video, .yowHoverPreviewModal video'
      );
      allVideos.forEach((v) => {
        v.muted = true;
        v.defaultMuted = true;
      });
    } catch (err) {
      console.error('Error muting all teasers:', err);
    }
  }
};
